/* eslint-disable @typescript-eslint/no-explicit-any */
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { ExportPdfModal } from "@/Components/ExportPdfModal/ExportPdfModal";
import { useMediaQuery } from "@mantine/hooks";
import dayjs from "dayjs";
import { capitalize } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useCalendarStore } from "../store/useCalendarStore";
import { LimeAutocomplete } from "@/Components/NextBase/LimeAutocomplete";
import {
  Accordion,
  AccordionItem,
  AutocompleteItem,
  Button,
  Checkbox,
  CheckboxGroup,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  Tooltip,
} from "@heroui/react";
import { i18n } from "@lingui/core";
import { CalendarCog, CheckCircle, TriangleAlert } from "lucide-react";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { cn } from "@/utils";
import { LimeStepperNumberInput } from "@/Components/NextBase/LimeStepperNumberInput";
import useFeature from "@/hooks/useFeature";

export const Filter = ({
  IS_GLOWUP_USER,
  IS_ILLUME_USER,
}: {
  IS_GLOWUP_USER: boolean;
  IS_ILLUME_USER: boolean;
}) => {
  const {
    filterItems,
    users,
    services,
    locations,
    refreshWithNewFilter,
    locationActions,
    fetchingCalendarData,
    refreshCalendar,
  } = useCalendarStore((state) => state);

  const [opened, setOpened] = useState(false);

  const [filter, setFilter] = useState<any>({});

  useEffect(() => {
    setFilter(JSON.parse(JSON.stringify(filterItems)));
  }, [filterItems]);

  const openFilter = () => {
    setFilter(JSON.parse(JSON.stringify(filterItems)));
    setOpened(true);
  };

  const closeFilter = () => {
    setFilter(JSON.parse(JSON.stringify(filterItems)));
    setOpened(false);
  };

  const saveFilter = () => {
    refreshWithNewFilter(filter);
    setOpened(false);
  };

  const hasUsersSelected = filter?.users?.length > 0;
  const hasServiceSelected = filter?.services?.length > 0;
  const hasDaySelected = filter?.days?.length > 0;
  const hasResourcesSelected = filter?.resources?.length > 0;

  const canSave = useMemo(
    () =>
      (filter.calendarMode !== "users" || hasUsersSelected) &&
      hasServiceSelected &&
      hasDaySelected &&
      (filter.calendarMode !== "resources" || hasResourcesSelected),
    [
      filter,
      hasUsersSelected,
      hasServiceSelected,
      hasDaySelected,
      hasResourcesSelected,
    ],
  );

  const isDesktop = useMediaQuery("(min-width: 768px)");

  return (
    <div>
      <Button onPress={openFilter} isIconOnly variant="flat">
        <CalendarCog size={16} />
      </Button>

      {locations?.length > 0 && (
        <Drawer
          placement={isDesktop ? "right" : "bottom"}
          isOpen={opened}
          onClose={closeFilter}
        >
          <DrawerContent
            className={"drawer-outer-content"}
            style={{ overflowY: "scroll" }}
          >
            <DrawerHeader>
              <Trans>Filter</Trans>
            </DrawerHeader>
            <DrawerBody>
              <FilterContent
                filterItems={filter}
                setFilterItems={setFilter}
                users={users}
                services={services.filter((s: any) => s.permission)}
                locations={locations}
                locationActions={locationActions}
                isLoading={fetchingCalendarData}
                hasUsersSelected={hasUsersSelected}
                hasServiceSelected={hasServiceSelected}
                hasDaySelected={hasDaySelected}
                refreshCalendar={refreshCalendar}
                hasResourcesSelected={hasResourcesSelected}
              ></FilterContent>

              {!IS_GLOWUP_USER && !IS_ILLUME_USER && (
                <>
                  <ExportPdfModal></ExportPdfModal>
                </>
              )}
            </DrawerBody>
            <DrawerFooter>
              <Button onPress={closeFilter} variant="flat">
                <Trans>Prekliči</Trans>
              </Button>
              <Button
                isDisabled={!canSave}
                onPress={saveFilter}
                color="primary"
              >
                <Trans>Shrani</Trans>
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      )}
    </div>
  );
};

const FilterContent = ({
  filterItems,
  users,
  services,
  locations,
  setFilterItems,
  locationActions,
  isLoading,
  hasDaySelected,
  hasServiceSelected,
  hasUsersSelected,
  refreshCalendar,
  hasResourcesSelected,
}: {
  filterItems: any;
  users: any;
  services: any;
  locations: any;
  setFilterItems: any;
  locationActions: any;
  isLoading: boolean;
  hasDaySelected: boolean;
  hasServiceSelected: boolean;
  hasUsersSelected: boolean;
  refreshCalendar: any;
  hasResourcesSelected: boolean;
}) => {
  const { resources } = useCalendarStore((state) => state);

  const { isFeatureEnabled } = useFeature();

  const locale = i18n.locale;

  const days = [...Array(7).keys()].map((i) => {
    const day = dayjs().locale(locale).startOf("week").add(i, "day");
    return {
      dayId: day.day(),
      name: capitalize(day.format("dddd")),
    };
  }) as any[];

  if (days[0].dayId === 0) {
    const firstDay = days.shift();
    days.push(firstDay);
  }

  const setLocation = (location: any) => {
    setFilterItems((prev: any) => {
      const newState: any = JSON.parse(JSON.stringify(prev));
      newState.FK_locationId = location;
      return newState;
    });
    locationActions.setSelectedLocationId(location);
    refreshCalendar();
  };

  const selectAllUsers = () => {
    if (users.every((s: any) => filterItems.users.includes(s.userId))) {
      setFilterItems((prev: any) => {
        const newState: any = JSON.parse(JSON.stringify(prev));
        newState.users = [];
        return newState;
      });
    } else {
      setFilterItems((prev: any) => {
        const newState: any = JSON.parse(JSON.stringify(prev));
        newState.users = users.map((s: any) => s.userId);
        return newState;
      });
    }
  };

  const selectAllResources = () => {
    if (resources.every((r: any) => filterItems.resources.includes(r.id))) {
      setFilterItems((prev: any) => {
        const newState: any = JSON.parse(JSON.stringify(prev));
        newState.resources = [];
        return newState;
      });
    } else {
      setFilterItems((prev: any) => {
        const newState: any = JSON.parse(JSON.stringify(prev));
        newState.resources = resources.map((r: any) => r.id);
        return newState;
      });
    }
  };

  const selectAllServices = () => {
    if (
      services.every((s: any) => filterItems.services.includes(s.serviceId))
    ) {
      setFilterItems((prev: any) => {
        const newState: any = JSON.parse(JSON.stringify(prev));
        newState.services = [];
        return newState;
      });
    } else {
      setFilterItems((prev: any) => {
        const newState: any = JSON.parse(JSON.stringify(prev));
        newState.services = services.map((s: any) => s.serviceId);
        return newState;
      });
    }
  };

  const selectAllDays = () => {
    if (filterItems.days.length === days.length) {
      setFilterItems((prev: any) => {
        const newState: any = JSON.parse(JSON.stringify(prev));
        newState.days = [];
        return newState;
      });
    } else {
      setFilterItems((prev: any) => {
        const newState: any = JSON.parse(JSON.stringify(prev));
        newState.days = days.map((s: any) => s.dayId);
        return newState;
      });
    }
  };

  const areResourcesEnabled = isFeatureEnabled("RESOURCES");

  return (
    <div>
      <LimeAutocomplete
        isVirtualized={false} // TODO: set to true after bugs are fixed (https://github.com/heroui-inc/heroui/issues/4494) , (https://github.com/heroui-inc/heroui/issues/4568)
        selectedKey={filterItems.FK_locationId?.toString()}
        size={"sm"}
        placeholder={t`Lokacija`}
        label={t`Lokacija`}
        variant="bordered"
        className="mb-2"
        scrollShadowProps={{
          isEnabled: false,
        }}
        onSelectionChange={(key) => {
          if (!key) return;

          setLocation(Number(key));
        }}
      >
        {locations?.map((location: any) => {
          return (
            <AutocompleteItem
              key={location.locationId.toString()}
              textValue={location.name}
            >
              {location.name}
            </AutocompleteItem>
          );
        })}
      </LimeAutocomplete>

      {areResourcesEnabled ? (
        <LimeSelect
          items={[
            { label: t`Zaposleni`, key: "users" },
            { label: t`Sredstva`, key: "resources" },
          ]}
          value={filterItems.calendarMode}
          onSingleItemChange={(value) => {
            setFilterItems((prev: any) => {
              const newState: any = JSON.parse(JSON.stringify(prev));
              newState.calendarMode = value;

              return newState;
            });
          }}
          variant="bordered"
          label={t`Pogled koledarja`}
          disallowEmptySelection={true}
        />
      ) : null}

      <Accordion variant="bordered" className="mt-2" aria-label="Filter">
        <AccordionItem
          textValue={t`Zaposleni`}
          key={"users"}
          title={
            <div className="flex items-center justify-between">
              <p>
                <Trans>Zaposleni</Trans>
              </p>
              {WarningIcon(
                t`Izbrati morate vsaj 1 zaposlenega`,
                filterItems.calendarMode === "users" && !hasUsersSelected,
              )}
            </div>
          }
          aria-label={t`Zaposleni`}
        >
          <ToggleAllButton
            onSelectAll={selectAllUsers}
            allSelected={users.every((u: any) =>
              filterItems.users.includes(u.userId),
            )}
          />

          <CheckboxGroup
            value={filterItems.users}
            onValueChange={(value) => {
              setFilterItems((prev: any) => {
                const newState: any = JSON.parse(JSON.stringify(prev));
                newState.users = value;
                return newState;
              });
            }}
          >
            {users.map((user: any, index: number) => (
              <CustomCheckbox
                key={index}
                value={user.userId}
                label={`${user.name} ${user.lastName}`}
              />
            ))}
          </CheckboxGroup>
        </AccordionItem>

        {areResourcesEnabled ? (
          <AccordionItem
            key={"resources"}
            textValue={t`Sredstva`}
            title={
              <div className="flex items-center justify-between">
                <p>
                  <Trans>Sredstva</Trans>
                </p>
                {WarningIcon(
                  t`Izbrati morate vsaj 1 sredstvo`,
                  filterItems.calendarMode === "resources" &&
                    !hasResourcesSelected,
                )}
              </div>
            }
          >
            <ToggleAllButton
              allSelected={resources.every((r: any) =>
                filterItems.resources?.includes(r.id),
              )}
              onSelectAll={selectAllResources}
            />

            <CheckboxGroup
              value={filterItems.resources}
              onValueChange={(value) => {
                setFilterItems((prev: any) => {
                  const newState: any = JSON.parse(JSON.stringify(prev));
                  newState.resources = value;
                  return newState;
                });
              }}
            >
              {resources.map((resource: any, index: number) => (
                <CustomCheckbox
                  key={index}
                  value={resource.id}
                  label={`${resource.label}`}
                />
              ))}
            </CheckboxGroup>
          </AccordionItem>
        ) : null}

        <AccordionItem
          key={"service"}
          textValue={t`Storitve`}
          title={
            <div className="flex items-center justify-between">
              <p>
                <Trans>Storitve</Trans>
              </p>
              {WarningIcon(
                "Izbrati morate vsaj 1 storitev",
                !hasServiceSelected,
              )}
            </div>
          }
        >
          <ToggleAllButton
            allSelected={services.every((s: any) =>
              filterItems.services.includes(s.serviceId),
            )}
            onSelectAll={selectAllServices}
          />

          <CheckboxGroup
            value={filterItems.services}
            onValueChange={(value) => {
              setFilterItems((prev: any) => {
                const newState: any = JSON.parse(JSON.stringify(prev));
                newState.services = value;
                return newState;
              });
            }}
          >
            {services.map((service: any, index: number) => (
              <CustomCheckbox
                key={index}
                value={service.serviceId}
                label={service.name}
                color={service.color ?? "#74B587"}
              />
            ))}
          </CheckboxGroup>
        </AccordionItem>

        <AccordionItem
          key={"days"}
          textValue={t`Prikazani dnevi`}
          title={
            <div className="flex items-center justify-between">
              <p>
                <Trans>Prikazani dnevi</Trans>
              </p>
              {WarningIcon(t`Izbrati morate vsaj 1 dan`, !hasDaySelected)}
            </div>
          }
        >
          <ToggleAllButton
            allSelected={filterItems.days.length === days.length}
            onSelectAll={selectAllDays}
          />

          <CheckboxGroup
            value={filterItems.days}
            onValueChange={(value) => {
              setFilterItems((prev: any) => {
                const newState: any = JSON.parse(JSON.stringify(prev));
                newState.days = value;
                return newState;
              });
            }}
          >
            {days.map((day, index) => (
              <CustomCheckbox key={index} value={day.dayId} label={day.name} />
            ))}
          </CheckboxGroup>
        </AccordionItem>

        <AccordionItem
          key={"calendar_settings"}
          textValue={t`Nastavitve koledarja`}
          title={t`Nastavitve koledarja`}
        >
          <div className="flex items-center justify-between">
            <LimeStepperNumberInput
              value={filterItems.time_step}
              label={t`Časovni korak`}
              disabled={false}
              onValueChange={(value: number) => {
                setFilterItems((prev: any) => {
                  const newState: any = JSON.parse(JSON.stringify(prev));
                  newState.time_step = value;
                  return newState;
                });
              }}
              steppersProps={{
                variant: "flat",
                size: "sm",
              }}
              size="sm"
              step={5}
              min={5}
              max={300}
              variant="flat"
              endContent={<span className="text-sm">{t`min`}</span>}
            />
          </div>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

const WarningIcon = (tooltip: string, active: boolean) => {
  if (!active) return null;

  return (
    <Tooltip content={tooltip} showArrow>
      <TriangleAlert size={20} className="text-danger" />
    </Tooltip>
  );
};

const ToggleAllButton = ({
  onSelectAll,
  allSelected,
}: {
  onSelectAll: () => void;
  allSelected: boolean;
}) => {
  return (
    <Button
      variant="flat"
      onPress={onSelectAll}
      size="sm"
      className={cn("mb-2", {
        "bg-primary text-white": allSelected,
        "text-gray-500": !allSelected,
      })}
      color={allSelected ? "primary" : "default"}
      startContent={<CheckCircle size={16} />}
    >
      <Trans>Izberi vse</Trans>
    </Button>
  );
};

const CustomCheckbox = ({
  value,
  label,
  color,
}: {
  value: string;
  label: string;
  color?: string;
}) => {
  return (
    <Checkbox
      aria-label={value}
      classNames={{
        base: cn(
          "inline-flex max-w-md w-full bg-content1 m-0",
          "hover:bg-content2 items-center justify-start",
          "cursor-pointer rounded-lg gap-1 p-2",
          "border-2 border-default",
          "data-[selected=true]:border-primary",
        ),
        label: "w-full",
      }}
      value={value}
    >
      <div className="flex w-full items-center justify-between gap-2">
        <div className="flex flex-col items-end gap-1">
          <span className="text-sm">{label}</span>
        </div>
        <div
          className="h-4 w-4 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
        ></div>
      </div>
    </Checkbox>
  );
};
